import { useMutation, useQuery } from "@tanstack/react-query"
import {
  ClientOnly,
  createFileRoute,
  useStableCallback,
} from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  type ColumnDef,
  createColumnHelper,
  type VisibilityState,
} from "@tanstack/react-table"
import { PencilIcon, PlusIcon, PowerIcon } from "lucide-react"
import { useId, useMemo, useState } from "react"

import {
  ActionsDropdownMenu,
  type ActionsDropdownMenuGroup,
} from "@repo/ui/components/ui/actions-dropdown-menu"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { DataTable } from "@repo/ui/components/ui/data-table"
import { PageDetailsPanel } from "@repo/ui/components/ui/page-details-panel"
import { toast } from "@repo/ui/components/ui/sonner"
import { useLocalStorage } from "@repo/ui/hooks/use-local-storage"
import { AddLanguageProficiencyLevelModal } from "~/modules/system-config/components/add-language-proficiency-level-modal"
import { EditLanguageProficiencyLevelModal } from "~/modules/system-config/components/edit-language-proficiency-level-modal"
import { updateLanguageProficiencyLevelStatusFn } from "~/modules/system-config/system-config.functions"
import { getAvailableLanguageProficiencyLevelsQueryOptions } from "~/modules/system-config/system-config.queries"
import type { LanguageProficiencyLevel } from "~/modules/system-config/system-config.service"
import { ComponentErrorBoundary } from "~/root-components/component-error-boundary"

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/_admin-layout/system-config/language-proficiency-levels",
)({
  component: SystemConfigLanguageProficiencyLevels,
})

const columnHelper = createColumnHelper<LanguageProficiencyLevel>()
const TABLE_VISIBLE_COLUMNS_KEY =
  "system-config-language-proficiency-levels-visible-columns"

function SystemConfigLanguageProficiencyLevels() {
  const toastId = useId()

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedProficiencyLevel, setSelectedProficiencyLevel] =
    useState<LanguageProficiencyLevel | null>(null)
  const [visibleColumns, setVisibleColumns] = useLocalStorage<VisibilityState>(
    TABLE_VISIBLE_COLUMNS_KEY,
    {},
  )

  const { data: proficiencyLevels = [], isLoading } = useQuery(
    getAvailableLanguageProficiencyLevelsQueryOptions(["active", "inactive"]),
  )

  const updateProficiencyLevelStatus = useServerFn(
    updateLanguageProficiencyLevelStatusFn,
  )

  const updateStatusMutation = useMutation({
    meta: {
      invalidates: [
        getAvailableLanguageProficiencyLevelsQueryOptions().queryKey,
      ],
    },
    mutationFn: updateProficiencyLevelStatus,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Proficiency level status updated.", { id: toastId })
      } else {
        toast.error(result.message, { id: toastId })
      }
    },
    onError: (error) => {
      toast.error(error.message, { id: toastId })
    },
  })

  const getActionsGroups = useStableCallback(
    (
      proficiencyLevel: LanguageProficiencyLevel,
    ): ActionsDropdownMenuGroup[] => [
      {
        actions: [
          {
            label: "Edit Proficiency Level",
            icon: PencilIcon,
            onClick: () => {
              setSelectedProficiencyLevel(proficiencyLevel)
              setShowEditModal(true)
            },
          },
        ],
      },
      {
        actions: [
          {
            label:
              proficiencyLevel.status === "active"
                ? "Disable Proficiency Level"
                : "Enable Proficiency Level",
            icon: PowerIcon,
            onClick: () => {
              toast.loading("Updating status...", { id: toastId })
              updateStatusMutation.mutate({
                data: {
                  code: proficiencyLevel.code,
                  status:
                    proficiencyLevel.status === "active"
                      ? "inactive"
                      : "active",
                },
              })
            },
            variant:
              proficiencyLevel.status === "active"
                ? ("destructive" as const)
                : ("success" as const),
          },
        ],
      },
    ],
  )

  // biome-ignore lint/suspicious/noExplicitAny: please
  const columns: ColumnDef<LanguageProficiencyLevel, any>[] = useMemo(
    () => [
      columnHelper.accessor("code", {
        id: "code",
        header: () => <div className="min-w-[80px]">Code</div>,
        cell: (info) => (
          <span className="font-mono text-white">{info.getValue()}</span>
        ),
        meta: { columnName: "Code" },
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="min-w-[120px]">Name</div>,
        cell: (info) => <span className="text-white">{info.getValue()}</span>,
        meta: { columnName: "Name" },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="w-[100px]">Status</div>,
        cell: (info) => (
          <Badge
            variant={info.getValue() === "active" ? "default" : "secondary"}
            className={
              info.getValue() === "active"
                ? "bg-green-500/20 text-green-400"
                : "bg-gray-500/20 text-gray-400"
            }
          >
            {info.getValue() === "active" ? "Active" : "Disabled"}
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          filterSelectOptions: [
            { label: "Active", value: "active" },
            { label: "Disabled", value: "inactive" },
          ],
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="grow text-right">Actions</div>,
        enableHiding: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <ActionsDropdownMenu groups={getActionsGroups(row.original)} />
          </div>
        ),
      }),
    ],
    [getActionsGroups],
  )

  return (
    <PageDetailsPanel
      title="Language Proficiency Levels"
      actions={
        <Button onClick={() => setShowAddModal(true)}>
          <PlusIcon className="mr-2 h-4 w-4" />
          Add Proficiency Level
        </Button>
      }
    >
      <div className="flex-1 overflow-auto p-3">
        <DataTable
          data={proficiencyLevels}
          columns={columns}
          searchPlaceholder="Search proficiency levels..."
          noResultsMessage={
            isLoading ? "Loading..." : "No proficiency levels found"
          }
          manualPagination={false}
          manualSorting={false}
          manualFiltering={false}
          getInitialColumnVisibility={() => visibleColumns}
          onColumnVisibilityChange={setVisibleColumns}
        />
      </div>

      <ClientOnly>
        <ComponentErrorBoundary>
          <AddLanguageProficiencyLevelModal
            open={showAddModal}
            onOpenChange={setShowAddModal}
          />

          {selectedProficiencyLevel && (
            <EditLanguageProficiencyLevelModal
              key={selectedProficiencyLevel.id}
              open={showEditModal}
              onOpenChange={setShowEditModal}
              proficiencyLevel={selectedProficiencyLevel}
            />
          )}
        </ComponentErrorBoundary>
      </ClientOnly>
    </PageDetailsPanel>
  )
}
